import React, { LegacyRef, useState } from "react";
import toast from "react-hot-toast";
import { authApiService } from "../../../../api/api";
import { ReactComponent as PhotoIcon } from "../../../../assets/icons/modal/emptyPhoto.svg";
import {
  BlogStatus,
  BlogType,
  IBlogCreateDto,
} from "../../../../types/blogs.type";
import SmallWhiteButton from "../../../buttons/WhiteButton/SmallWhiteButton";
import TinyMceEditor from "../../../TinyMceEditor/TinyMceEditor";
import ModalBottomButtons from "../buttons/ModakBottomButtons/ModalBottomButtons";
import EditInput from "../inputs/EditInput/EditInput";
import SelectInput, { SelectOption } from "../inputs/SelectInput/SelectInput";
import styles from "./styles.module.scss";

interface Props {
  onClose: () => void;
  onApply: () => void;
}

const typeOptions: SelectOption[] = [
  { label: "Shop", value: BlogType.SHOP },
  { label: "DrEvol", value: BlogType.DREVOL },
  { label: "Evoluxe", value: BlogType.EVOLUXE },
];

const statusOptions: SelectOption[] = [
  { label: "Draft", value: BlogStatus.DRAFT },
  { label: "Published", value: BlogStatus.PUBLISHED },
];

const AddBlogForm: React.FC<Props> = (props) => {
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [content, setContent] = useState("");
  const [type, setType] = useState<SelectOption>(typeOptions[0]);
  const [status, setStatus] = useState<SelectOption>(statusOptions[0]);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fileInputRef: LegacyRef<HTMLInputElement> | undefined =
    React.createRef();

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setSelectedImage(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const removePhoto = () => {
    setSelectedImage(null);
    setPreviewUrl(null);
  };

  const onApply = async () => {
    if (!title.trim() || !slug.trim() || !content.trim()) {
      toast.error("Title, slug and content are required");
      return;
    }

    setIsSubmitting(true);
    try {
      let coverImageUrl: string | undefined;
      if (selectedImage) {
        const formData = new FormData();
        formData.append("file", selectedImage);
        const response = await authApiService.uploadImage(formData);
        coverImageUrl = response.publicUrl;
      }

      const body: IBlogCreateDto = {
        title: title.trim(),
        slug: slug.trim(),
        content,
        coverImageUrl,
        type: type.value as BlogType,
        status: status.value as BlogStatus,
      };

      const response = await authApiService.createBlog(body);
      if (response) {
        props.onApply();
        props.onClose();
      }
    } catch (err: any) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || "Something went wrong. Try again later"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.photoContainer}>
        {previewUrl ? (
          <img src={previewUrl} alt="cover" className={styles.image} />
        ) : (
          <PhotoIcon />
        )}
        <input
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          ref={fileInputRef}
          className={styles.input}
        />
        <SmallWhiteButton
          onClick={previewUrl ? removePhoto : handleButtonClick}
          title={previewUrl ? "Remove cover" : "Add cover"}
        />
      </div>

      <form className={styles.form}>
        <EditInput
          value={title}
          setValue={setTitle}
          title="Title"
          isRequired
        />
        <EditInput value={slug} setValue={setSlug} title="Slug" isRequired />
        <SelectInput
          title="Type"
          value={type}
          values={typeOptions}
          onChange={setType}
        />
        <SelectInput
          title="Status"
          value={status}
          values={statusOptions}
          onChange={setStatus}
        />
        <TinyMceEditor value={content} onChange={setContent} />
      </form>

      <ModalBottomButtons
        onPressBack={props.onClose}
        onPressApply={onApply}
        backTitle="Back"
        applyTitle={isSubmitting ? "Saving..." : "Add blog"}
      />
    </div>
  );
};

export default AddBlogForm;
